// Org analytics derivations for the home orbit canvas. Pure functions over data
// useBrainData already has (hierarchy tree, member metrics, move queue), so the
// canvas gains manager-grade numbers without a single new request.
// Self-check: node src/pages/DashboardV2/screens/brain/brainAnalytics.check.mjs

/**
 * Red-flag count per member, from the move queue. Member-scoped moves that
 * aren't growth signals (idle drags, AI anomalies) count; org-wide and
 * project moves don't belong to any one branch.
 */
export function riskByMember(moves = []) {
  const map = new Map();
  for (const mv of moves) {
    if (mv?.subject?.type !== "member" || mv.severity === "growth") continue;
    const id = String(mv.subject.id);
    map.set(id, (map.get(id) || 0) + 1);
  }
  return map;
}

/**
 * Post-order rollup over the hierarchy: for every node, the tracked minutes
 * and red flags of their whole branch (themselves + everyone below). Runs over
 * the full tree once, so a collapsed branch still reports its real totals.
 *
 * @param {Array} roots  hierarchy nodes ({ id, reports })
 * @param {Map<string, {totalMinutes?: number}>} memberById
 * @param {Map<string, number>} riskMap  from riskByMember
 * @returns {Map<string, { teamMinutes: number, risk: number }>}
 */
export function subtreeStats(roots = [], memberById = new Map(), riskMap = new Map()) {
  const stats = new Map();
  const walk = (node) => {
    const id = String(node.id);
    let teamMinutes = memberById.get(id)?.totalMinutes || 0;
    let risk = riskMap.get(id) || 0;
    for (const child of node.reports || []) {
      const c = walk(child);
      teamMinutes += c.teamMinutes;
      risk += c.risk;
    }
    const entry = { teamMinutes, risk };
    stats.set(id, entry);
    return entry;
  };
  roots.forEach(walk);
  return stats;
}

/**
 * The org's shape, for the header line: reporting depth, widest generation,
 * average direct reports across managers, and how many people have no manager
 * (roots minus org_admins — a CEO legitimately has no manager).
 */
export function orgShape(roots = []) {
  let total = 0, depth = 0, widest = 0, managers = 0, spanSum = 0, unmanaged = 0;
  const widthByDepth = new Map();
  const walk = (node, d) => {
    total += 1;
    depth = Math.max(depth, d);
    widthByDepth.set(d, (widthByDepth.get(d) || 0) + 1);
    const kids = (node.reports || []).length;
    if (kids > 0) { managers += 1; spanSum += kids; }
    (node.reports || []).forEach((c) => walk(c, d + 1));
  };
  roots.forEach((r) => {
    if (r.role !== "org_admin") unmanaged += 1;
    walk(r, 1);
  });
  widthByDepth.forEach((w) => { widest = Math.max(widest, w); });
  return {
    total, depth, widest, unmanaged,
    avgSpan: managers ? Math.round((spanSum / managers) * 10) / 10 : 0,
  };
}

/** New-join window: joined within the last 30 days. */
export const isNewJoin = (joinedAt, now = Date.now()) =>
  !!joinedAt && now - new Date(joinedAt).getTime() < 30 * 864e5;
